/**
 * API Service Layer
 * Centralized HTTP client using Axios
 *
 * Key Features:
 * - Token refresh with mutex to prevent race conditions
 * - CSRF token fetch deduplication
 * - Proper error handling and retry logic
 */

import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import type { ApiResponse, ApiError } from '../types';
import { isMarketingDomain } from '../hooks/useDomainRedirect';

// 3100, not 3000: OKR Hub owns the 31xx port block (see CLAUDE.md § Ports). Port
// 3000 is the Marketing Hub app, which ANSWERS rather than refusing, so a 30xx
// fallback would silently send this app's requests — credentials included — there.
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3100/api';

// Create Axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Allow cookies to be sent with requests
});

// ============================================================================
// CSRF Token Management with Deduplication
// ============================================================================

// Store CSRF token
let csrfToken: string | null = null;

// Promise for in-flight CSRF token fetch (prevents duplicate requests)
let csrfFetchPromise: Promise<string> | null = null;

/**
 * Fetch CSRF token from server with deduplication
 * If a fetch is already in progress, returns the existing promise
 */
const fetchCsrfToken = async (): Promise<string> => {
  // If already fetching, return the existing promise to prevent duplicate requests
  if (csrfFetchPromise) {
    return csrfFetchPromise;
  }

  csrfFetchPromise = (async () => {
    try {
      const response = await axios.get<ApiResponse>(`${API_URL}/csrf-token`, {
        withCredentials: true,
      });
      if (response.data.success && response.data.data) {
        csrfToken = (response.data.data as { csrfToken: string }).csrfToken;
        return csrfToken;
      }
      throw new Error('Failed to fetch CSRF token');
    } catch (error) {
      console.error('Error fetching CSRF token:', error);
      throw error;
    } finally {
      // Clear the promise so next call will fetch fresh
      csrfFetchPromise = null;
    }
  })();

  return csrfFetchPromise;
};

// Fetch CSRF token on app initialization — but NOT on the marketing site.
//
// hr4india.com / www.hr4india.com are served from S3+CloudFront with no backend, and the
// SPA fallback answers /api/csrf-token with index.html at status 200. That is not a network
// failure, so axios resolves, `response.data.success` is undefined, and fetchCsrfToken throws
// "Failed to fetch CSRF token" — logging an error on every landing-page load. Nothing there
// is state-changing, and the request interceptor below fetches the token on demand for the
// requests that need it, so skipping the eager fetch costs nothing on either domain.
if (!isMarketingDomain()) {
  fetchCsrfToken().catch((err) => {
    console.warn('Failed to fetch CSRF token on initialization:', err);
  });
}

// ============================================================================
// Token Refresh Management with Mutex
// ============================================================================

// Flag to indicate if a token refresh is in progress
let isRefreshing = false;

// Queue of failed requests waiting for token refresh
type FailedRequest = {
  resolve: (value: unknown) => void;
  reject: (error: unknown) => void;
  config: AxiosRequestConfig;
};
let failedRequestsQueue: FailedRequest[] = [];

// Flag to prevent multiple redirects to login
let isRedirectingToLogin = false;

/**
 * Process queued requests after token refresh
 * Retries all queued requests in parallel after successful refresh,
 * or rejects all with error if refresh failed.
 * @param error - If provided, reject all queued requests with this error
 */
const processQueue = (error: unknown = null): void => {
  failedRequestsQueue.forEach((request) => {
    try {
      if (error) {
        request.reject(error);
      } else {
        // Retry the request with the new token
        request.resolve(apiClient(request.config));
      }
    } catch (err) {
      // If resolve/reject throws, ensure we still reject the request
      request.reject(err);
    }
  });
  failedRequestsQueue = [];
};

/**
 * Handle logout - clear state and redirect to login
 * Uses a flag to prevent multiple redirects during concurrent 401 errors
 */
const handleLogout = (): void => {
  if (isRedirectingToLogin) {
    return; // Already redirecting, don't do it again
  }
  isRedirectingToLogin = true;
  localStorage.removeItem('user');

  // Use setTimeout to allow current call stack to complete
  setTimeout(() => {
    try {
      window.location.href = '/login';
    } catch (error) {
      // Reset flag if redirect fails (e.g., in test environments)
      console.error('Failed to redirect to login:', error);
      isRedirectingToLogin = false;
    }
  }, 0);
};

// ============================================================================
// Request Interceptor
// ============================================================================

// Request interceptor - Add CSRF token to state-changing requests
apiClient.interceptors.request.use(
  async (config) => {
    // Add CSRF token to state-changing requests (POST, PUT, PATCH, DELETE)
    const stateChangingMethods = ['post', 'put', 'patch', 'delete'];
    if (config.method && stateChangingMethods.includes(config.method.toLowerCase())) {
      // Fetch CSRF token if not already fetched
      if (!csrfToken) {
        try {
          await fetchCsrfToken();
        } catch (error) {
          console.error('Failed to fetch CSRF token for request:', error);
        }
      }

      // Add CSRF token to headers
      if (csrfToken && config.headers) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ============================================================================
// Response Interceptor
// ============================================================================

// List of auth endpoints that should NOT trigger token refresh on 401
const authEndpoints = [
  '/auth/login',
  '/auth/register',
  '/auth/activate',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/accept-invitation',
  '/auth/refresh',
  '/auth/check-organization',
  '/csrf-token',
];

// Check if a URL is an auth endpoint
const isAuthEndpoint = (url?: string): boolean => {
  if (!url) return false;
  return authEndpoints.some(endpoint => url.includes(endpoint));
};

// Response interceptor - Handle errors and token refresh
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;      // Flag for 401 token refresh retry
      _csrfRetry?: boolean;  // Separate flag for 403 CSRF retry
    };

    // For auth endpoints, don't try to refresh - just pass through the error
    // This prevents page refresh and allows error messages to be displayed properly
    if (isAuthEndpoint(originalRequest.url)) {
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized - Try to refresh token (only for protected endpoints)
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Mark this request as retried to prevent infinite loops
      originalRequest._retry = true;

      // If already refreshing, queue this request to retry after refresh completes
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({
            resolve,
            reject,
            config: originalRequest,
          });
        });
      }

      // Start the refresh process
      isRefreshing = true;

      try {
        // Refresh token is in httpOnly cookie, no need to pass it in body
        const response = await axios.post<ApiResponse>(
          `${API_URL}/auth/refresh`,
          {},
          { withCredentials: true }
        );

        if (response.data.success) {
          // Token refresh successful - process queued requests
          processQueue();

          // Retry the original request
          return apiClient(originalRequest);
        } else {
          // Refresh returned success: false - treat as failure
          throw new Error('Token refresh failed');
        }
      } catch (refreshError) {
        // Refresh failed - reject all queued requests
        processQueue(refreshError);

        // Clear user data and redirect to login (only once)
        handleLogout();

        return Promise.reject(refreshError);
      } finally {
        // Always reset the refreshing flag
        isRefreshing = false;
      }
    }

    // Handle 403 Forbidden - CSRF token might be invalid/expired
    if (error.response?.status === 403 && error.response?.data?.message?.includes('CSRF')) {
      // Only retry once for CSRF errors (use separate flag from token refresh)
      if (!originalRequest._csrfRetry) {
        originalRequest._csrfRetry = true;
        try {
          // Clear existing token to force refetch
          csrfToken = null;
          await fetchCsrfToken();
          return apiClient(originalRequest);
        } catch (csrfError) {
          console.error('Failed to refresh CSRF token:', csrfError);
        }
      }
    }

    return Promise.reject(error);
  }
);

// ============================================================================
// Helper Functions
// ============================================================================

// Helper function to extract error message with user-friendly permission messages
export const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiError>;
    const status = axiosError.response?.status;
    const message = axiosError.response?.data?.message;

    // Handle 403 Forbidden with user-friendly messages
    if (status === 403) {
      // Check if it's a permission-related error
      if (message?.includes('do not have permission')) {
        // Extract the action and resource from the message
        // Message format: "You do not have permission to [action] [resource]"
        const match = message.match(/to (\w+) (\w+)/);
        if (match) {
          const action = match[1];
          const resource = match[2];
          return getPermissionDeniedMessage(action, resource);
        }
        return 'You do not have permission to perform this action. Please contact your administrator if you need access.';
      }
      return message || 'Access denied. You do not have the required permissions.';
    }

    // Return the server message if available
    if (message) {
      return message;
    }

    // Network or other errors
    if (axiosError.message) {
      return axiosError.message;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

// Helper to generate user-friendly permission denied messages
const getPermissionDeniedMessage = (action: string, resource: string): string => {
  const resourceNames: Record<string, string> = {
    okr: 'OKR',
    key_result: 'Key Result',
    user: 'user',
    approval: 'approval',
    comment: 'comment',
    settings: 'settings',
    report: 'report',
  };

  const actionNames: Record<string, string> = {
    create: 'create',
    read: 'view',
    update: 'edit',
    delete: 'delete',
    export: 'export',
  };

  const resourceName = resourceNames[resource] || resource;
  const actionName = actionNames[action] || action;

  return `You don't have permission to ${actionName} ${resourceName}s. Please contact your administrator if you need access.`;
};

// Helper function to extract validation errors
export const getValidationErrors = (error: unknown): Record<string, string> => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiError>;
    if (axiosError.response?.data?.errors) {
      return axiosError.response.data.errors.reduce(
        (acc, err) => {
          acc[err.field] = err.message;
          return acc;
        },
        {} as Record<string, string>
      );
    }
  }
  return {};
};

export default apiClient;
export const api = apiClient;

/**
 * Return the current CSRF token, fetching it from the server if we don't have
 * one cached. Used by callers that bypass the axios interceptor (e.g. the
 * fetch-based SSE streamer for AI chat) but still need CSRF coverage.
 */
export const getCsrfToken = async (): Promise<string> => {
  if (csrfToken) return csrfToken;
  return await fetchCsrfToken();
};

export const getApiBaseUrl = (): string => API_URL;
